/* import __COLOCATED_TEMPLATE__ from './change-conversation-priority.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import type { default as ChangeConversationPriorityStep } from 'embercom/models/operator/visual-builder/step/change-conversation-priority';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import {
  PRIORITY_LEVELS,
  PRIORITY_LEVEL_PICKER_ORDER,
  priorityLevelDisplay,
  type PriorityLevelName,
} from 'embercom/lib/inbox/priority-levels';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    step: ChangeConversationPriorityStep;
    editorState: EditorState;
    onChange?: () => void;
    onDelete?: () => void;
    readOnly?: boolean;
  };
}

// Static i18n keys per level — the ember skill forbids template-literal key
// construction. Shared with the macro composer picker.
const LEVEL_LABEL_KEYS: Record<PriorityLevelName, string> = {
  none: 'inbox.command-k.set-priority-level.none',
  low: 'inbox.command-k.set-priority-level.low',
  medium: 'inbox.command-k.set-priority-level.medium',
  high: 'inbox.command-k.set-priority-level.high',
  urgent: 'inbox.command-k.set-priority-level.urgent',
};

// Workflow visual-builder node for the `change-conversation-priority` step.
//
// Flag OFF: renders the legacy "Mark as priority" row — unchanged from before
// this issue. Backend defaults to URGENT when actionData has no `priority_level`.
//
// Flag ON: renders an IcSelect with all five levels (Urgent/High/Medium/Low/None).
// Selection writes both `priority` (legacy/macro splat-dispatcher path) and
// `priority_level` (rules contract). The dual write keeps backwards compat
// without forking the wire format — see model `createNewStep` for the rationale.
export default class ChangeConversationPriorityNodeItem extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare store: Store;

  // Workflow visual builder lives in Settings → Automation (non-Inbox). The
  // Ember skill mandates `app` service (not `session`) outside Inbox routes.
  // Mirrors the FF check pattern in sibling step components like `fin-custom`,
  // `if-branch`, and `fin/settings`.
  get isMultiLevelEnabled() {
    return this.appService.app.canUseFeature('team-inbox-priority-levels');
  }

  get priorityLevels() {
    return PRIORITY_LEVEL_PICKER_ORDER.map((value) => {
      let display = priorityLevelDisplay(value);
      return {
        value,
        text: this.intl.t(LEVEL_LABEL_KEYS[display.name]),
      };
    });
  }

  // Reads either the new `priority_level` integer or the legacy `priority`
  // integer. Older workflows persisted without either key default to URGENT
  // — matches the backend `resolved_priority_level` default.
  get selectedPriority(): number {
    let data = this.args.step.action?.actionData ?? {};
    if (typeof data.priority_level === 'number') {
      return data.priority_level;
    }
    if (typeof data.priority === 'number') {
      return data.priority;
    }
    return PRIORITY_LEVELS.PRIORITY;
  }

  get label() {
    let display = priorityLevelDisplay(this.selectedPriority);
    return this.intl.t(LEVEL_LABEL_KEYS[display.name]);
  }

  get isNoneLevel() {
    return this.selectedPriority === PRIORITY_LEVELS.NONE;
  }

  @action onSelectPriority(value: number) {
    // Guard: legacy steps saved before this PR may have no embedded action fragment.
    // createNewStep always creates one, but users with existing workflows won't.
    // Lazy-initialize here so selection doesn't throw — mirrors createNewStep defaults.
    if (!this.args.step.action) {
      this.args.step.action = this.store.createFragment('rules/rule-action', {
        type: 'change-conversation-priority',
        actionData: {
          priority: PRIORITY_LEVELS.PRIORITY,
          priority_level: PRIORITY_LEVELS.PRIORITY,
        },
      });
    }
    this.args.step.action.actionData = {
      ...this.args.step.action.actionData,
      priority: value,
      priority_level: value,
    };
    if (this.args.onChange) {
      this.args.onChange();
    }
  }

  @action closePicker() {
    if (this.args.step.action) {
      this.args.step.action.justAdded = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::ChangeConversationPriority': typeof ChangeConversationPriorityNodeItem;
    'workflows/graph-editor/node-items/steps/change-conversation-priority': typeof ChangeConversationPriorityNodeItem;
  }
}
